import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * SidePanelContext - Manages the split-view panel (right pane).
 *
 * IMPORTANT: This is DIFFERENT from the left-hand chrome!
 * - SidePanel (this): Right-side split-view panel for attachments etc.
 * - NavRail + WorkspaceSidebar: Left-side navigation
 *
 * On large screens (lg+), opening the panel renders content in a
 * SplitViewContainer beside the main page. On small screens, callers
 * should navigate to the full-page route instead.
 *
 * Its ONE product use case: a note / protocol detail page opening one of its own
 * attachments, so the source file sits beside the note it was extracted into.
 * Everything else opens the attachment's full page — see `useOpenAttachment`,
 * which is the only thing that should be calling `onOpen`.
 */

export interface SidePanelContextType {
  isOpen: boolean;
  onOpen: (content: ReactNode) => void;
  onClose: () => void;
  content: ReactNode;
}

export const SidePanelContext = createContext<SidePanelContextType | null>(null);

export const useSidePanel = () => {
  const context = useContext(SidePanelContext);
  if (!context) {
    // Return a no-op implementation instead of throwing an error
    // This handles edge cases where the context might not be available yet
    return {
      isOpen: false,
      onOpen: () => {},
      onClose: () => {},
      content: null,
    };
  }
  return context;
};
